const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const clamp = (value, min = 0, max = 100) =>
  Math.min(Math.max(value, min), max);

export const FIELD_STATUS_ALL_CLASSIFICATIONS = "all";

export const isUuid = (value) =>
  typeof value === "string" && UUID_REGEX.test(value);

const addUniqueFieldFilter = (filters, filter) => {
  const [key, value] = Object.entries(filter)[0] || [];

  if (!key || value === undefined || value === null || value === "") {
    return;
  }

  if (!filters.some((item) => item[key] === value)) {
    filters.push({ [key]: value });
  }
};

const resolvePositiveInteger = (value) => {
  const numberValue = Number(value);

  if (!Number.isInteger(numberValue) || numberValue <= 0) {
    return null;
  }

  return numberValue;
};

export const resolveFieldUuid = (field) => {
  const candidates = [
    field?.field_uuid,
    field?.fieldUuid,
    field?.field_data_uuid,
    field?.fieldDataUuid,
    field?.field_data?.uuid,
    field?.fieldData?.uuid,
    field?.uuid,
    field?.id,
  ];

  return candidates.find((candidate) => isUuid(candidate)) || null;
};

export const resolveFieldDataFieldFilters = (field) => {
  const filters = [];
  const fieldDataId = resolvePositiveInteger(
    field?.field_data_id ?? field?.fieldDataId ?? field?.field_data?.id,
  );
  const fieldId = resolvePositiveInteger(field?.field_id ?? field?.fieldId);
  const id = resolvePositiveInteger(field?.id);
  const fieldUuid = resolveFieldUuid(field);
  const fieldDataName =
    field?.field_data_name || field?.fieldDataName || field?.field_data?.name;
  const fieldName = field?.field_name || field?.fieldName || field?.name;
  const shortFieldName =
    typeof fieldName === "string"
      ? fieldName.replace(/^campo\s+/i, "").trim()
      : null;

  addUniqueFieldFilter(filters, { field_id: fieldDataId });
  addUniqueFieldFilter(filters, { field_uuid: fieldUuid });
  addUniqueFieldFilter(filters, { field_id: fieldId });
  addUniqueFieldFilter(filters, { field_id: id });
  addUniqueFieldFilter(filters, { field_name: fieldDataName });
  addUniqueFieldFilter(filters, { field_name: fieldName });
  addUniqueFieldFilter(filters, { field_name: shortFieldName });

  return filters;
};

export const getFieldDataFilterParams = (fieldFilter = {}) => {
  const params = {};

  if (fieldFilter.field_id) {
    params.field_id = fieldFilter.field_id;
  }

  if (fieldFilter.field_uuid) {
    params.field_uuid = fieldFilter.field_uuid;
  }

  if (fieldFilter.field_name) {
    params.field_name = fieldFilter.field_name;
  }

  return params;
};

export const compactParams = (params = {}) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = value;
    }

    return acc;
  }, {});

export const normalizeImageDates = (dates = []) =>
  dates
    .map((item) => item?.image_date)
    .filter(Boolean)
    .sort((left, right) => new Date(left) - new Date(right));

export const getLatestImageDate = (dates = []) => {
  const normalizedDates = normalizeImageDates(dates);

  return normalizedDates[normalizedDates.length - 1] || null;
};

export const buildImageDateRange = (imageDate) => {
  if (!imageDate) {
    return { startTime: null, endTime: null };
  }

  const dateOnly = String(imageDate).split("T")[0];

  return {
    startTime: `${dateOnly}T00:00:00`,
    endTime: `${dateOnly}T23:59:59`,
  };
};

export const normalizeGeoJsonPoint = (geom) => {
  const coordinates = geom?.coordinates;

  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }

  const longitude = Number(coordinates[0]);
  const latitude = Number(coordinates[1]);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return [latitude, longitude];
};

export const normalizeFieldDataImage = (image) => {
  if (!image) {
    return null;
  }

  const position = normalizeGeoJsonPoint(image.geom);

  return {
    id: image.id,
    imageName: image.image_name,
    fieldId: image.field_id,
    width: image.width,
    height: image.height,
    classification: image.classification || "Non classificata",
    timestamp: image.timestamp,
    altitude: image.altitude,
    hasObjects: Boolean(image.has_objects),
    imageBase64: image.image_base64 || null,
    geom: image.geom,
    position,
    raw: image,
  };
};

export const normalizeFieldDataImages = (images = []) =>
  images.map(normalizeFieldDataImage).filter((image) => image?.position);

export const getClassificationCounts = (images = []) =>
  images.reduce((acc, image) => {
    const classification = image?.classification || "Non classificata";
    acc[classification] = (acc[classification] || 0) + 1;
    return acc;
  }, {});

export const getBoundingBoxStyle = (box) => {
  const centerX = Number(box?.box_center_x);
  const centerY = Number(box?.box_center_y);
  const boxWidth = Number(box?.box_width);
  const boxHeight = Number(box?.box_height);

  if (
    !Number.isFinite(centerX) ||
    !Number.isFinite(centerY) ||
    !Number.isFinite(boxWidth) ||
    !Number.isFinite(boxHeight)
  ) {
    return null;
  }

  const left = clamp((centerX - boxWidth / 2) * 100);
  const top = clamp((centerY - boxHeight / 2) * 100);
  const right = clamp((centerX + boxWidth / 2) * 100);
  const bottom = clamp((centerY + boxHeight / 2) * 100);

  return {
    left: `${left}%`,
    top: `${top}%`,
    width: `${Math.max(right - left, 0)}%`,
    height: `${Math.max(bottom - top, 0)}%`,
  };
};

export const getImageDetections = (detail) => [
  ...(detail?.identified_objects || []).map((object) => ({
    ...object,
    source: "identified",
  })),
  ...(detail?.labeled_objects || []).map((object) => ({
    ...object,
    source: "labeled",
  })),
];

export const getDetectionConfidence = (detection) => {
  const confidence = Number(
    detection?.confidence ??
      detection?.confidence_score ??
      detection?.score ??
      detection?.probability,
  );

  return Number.isFinite(confidence) ? confidence : null;
};

export const normalizeFieldDataImageDetail = (detail) => {
  if (!detail) {
    return null;
  }

  const image = normalizeFieldDataImage(detail.image);

  return {
    ...detail,
    image,
    detections: getImageDetections(detail),
  };
};

export const fetchAllFieldDataImages = async ({
  fieldFilter,
  startTime,
  endTime,
  classification,
  requestPage,
  limit = 100,
}) => {
  const images = [];
  let offset = 0;
  let total = null;

  do {
    const response = await requestPage({
      fieldFilter,
      startTime,
      endTime,
      classification,
      limit,
      offset,
    });

    const pageData = response?.data || [];
    images.push(...pageData);

    total = Number.isFinite(Number(response?.total))
      ? Number(response.total)
      : images.length;

    if (pageData.length === 0) {
      break;
    }

    offset += limit;
  } while (images.length < total);

  return images;
};
